import { onDelegatedClick, initAllModules, tryGlobalDataOrJson } from './functions.js';

/**
 * Usage:
 *   <repeater-element values="<?= h(json_encode([['id' => 1], ['id' => 2]])) ?>" id="repeater1">
 *       <table class="table table-condensed table-bordered small">
 *           <tbody x:repeater:container>
 *
 *           </tbody>
 *           <tbody x:repeater:empty>
 *               <tr>
 *                   <td colspan="99">Empty</td>
 *               </tr>
 *           </tbody>
 *           <script type="text/template">
 *               <tr x:repeater:row>
 *                   <td><%- model.id %></td>
 *                   <td><a href="" x:repeater:delete>Delete</a></td>
 *               </tr>
 *           </script>
 *       </table>
 *       <button x:repeater:add>Add</button>
 *   </repeater-element>
 *   <script>
 *       window.repeater1.addRow({
 *           id: 3
 *       });
 *   </script>
 * 
 * The template can be inside a script tag with type="text/template" (in the above example) or with data-template-selector with an external node as below:
 *     <repeater-element data-template-selector="#subtemplate"></repeater-element>
 *     <script type="text/template" id="subtemplate">
 *         <div x:repeater:row>x</div>
 *     </script>
 * 
 * @since 2024-04-18 Footer option
 * @since 2024-04-18 addRow returns the row
 * @since 2024-07-05 Nested repeater-element and external template
 * @since 2025-04-23 Initial values from _globalData
 */
export default class RepeaterElement extends HTMLElement {
    
    #args             = null;
    #initialValues    = null;
    #compiledTemplate = null;
    #container        = null;
    #footer           = null;
    #empty            = null;
    #lastIndex        = null;
    #itemsCount       = null;

    connectedCallback() {
        this.#args = Object.assign({}, this.dataset);
        this.#initialValues = tryGlobalDataOrJson(this.getAttribute('values') || this.dataset.values || '[]');

        let qs = this.querySelector.bind(this);

        this.#buildCompiledTemplate();

        this.#container         = qs('[x\\:repeater\\:container]');
        this.#footer            = qs('[x\\:repeater\\:footer]');
        this.#empty             = qs('[x\\:repeater\\:empty]');
        this.#lastIndex 		= -1;
        this.#itemsCount 	    = 0;	

        this.#fillInitialValues();
        this.#bind();

    }

    #buildCompiledTemplate() {
        let templateHtml;
        
        if (this.dataset.templateSelector) {
            templateHtml = document.querySelector(this.dataset.templateSelector).innerHTML;
        } else {
            let templateNode        = this.querySelector('template, script[type="text/template"]');
            templateHtml = templateNode.innerHTML;
        }

        this.#compiledTemplate  =  _.template(templateHtml);
    }

    #fillInitialValues() {
        let added = false;
        
        if (this.#initialValues) {
            this.#initialValues.forEach(value => {
                this.addRow(value);
                added = true;
            });
        }

        if (!added) {
            let quantity = parseInt(this.#args.initialRows);
            for (let i = 1; i <= quantity; i++) {
                this.addRow({  });
            }
        }

        this.#toggleEmpty();
    }

    addRow(value) {

        if (!value.index) {
            value.index = this.#getNextIndex();
        }

        this.#itemsCount++;

        let html = this.#compiledTemplate({
            index: value.index, 
            model: value
        });

        this.#container.insertAdjacentHTML('beforeend', html);

        let row = this.#container.lastElementChild;

        row.querySelectorAll('select').forEach(select => {
            var val = select.dataset.value;
            if (val !== '') {
                select.value = val;
            }
        });

        initAllModules(row);

        this.#toggleEmpty();

        this.#args.onAdd && this.#args.onAdd(value, row);

        return row;
        
    }

    countRows() {
        return this.#itemsCount;
    }

    #getNextIndex() {
        this.#lastIndex = this.#lastIndex + 1;
        return this.#lastIndex;
    }

    #toggleEmpty() {
        if (this.#itemsCount > 0) {
            this.#empty.style.display = 'none';
            this.#container.style.display = '';
            if (this.#footer) {
                this.#footer.style.display = '';
            }
        } else {
            this.#container.style.display = 'none';
            if (this.#footer) {
    		    this.#footer.style.display = 'none';
            }
            this.#empty.style.display = '';
        }
    }

    #bind() {

        onDelegatedClick(this, '*[x\\:repeater\\:delete]', (event) => {

            let closestRepeater = event.target.closest('repeater-element');
            if (closestRepeater !== this) { return; }

            event.preventDefault();

            let row = event.target.closest('*[x\\:repeater\\:row]');

            row.remove();
            
            this.#itemsCount--;

            this.#toggleEmpty();

            let changeEvent = new CustomEvent('change', {
                bubbles: true,
            });
            this.dispatchEvent(changeEvent);
            
        });

        onDelegatedClick(this, '*[x\\:repeater\\:deleteall]', (event) => {

            let closestRepeater = event.target.closest('repeater-element');
            if (closestRepeater !== this) { return; }

            if (!confirm('¿Estas seguro?')) {
                return false;
            }

            // var $rows = $parent.find('*[role=repeater-row]');
            
            // var values = $rows.map(function() {
            // 	return $(this).data('value');
            // })
            // $rows.remove();
            
            // itemsCount = 0;

            // toggleEmpty();

            // opts.onRemove && opts.onRemove(values);

            let changeEvent = new CustomEvent('change');
            this.dispatchEvent(changeEvent);

            return false;

        });

        onDelegatedClick(this, '*[x\\:repeater\\:add]', (event) => {

            let closestRepeater = event.target.closest('repeater-element');
            if (closestRepeater !== this) { return; }
            
            event.preventDefault();

            let quantity = event.target.getAttribute('x:repeater:add');

            quantity = quantity ? parseInt(quantity) : 1;

            for (let i = 1; i <= quantity; i++) {
                this.addRow({  });
            }

            let changeEvent = new CustomEvent('change');
            this.dispatchEvent(changeEvent);

        }); 

    }

};

window.customElements.define('repeater-element', RepeaterElement);